import { ThemeColorsV2 } from '#app/theme/index.js';
import { HelperText } from './HelperText.js';
import { Input } from './Input.js';
import { InputValueWrapper } from './InputValueWrapper.js';
import { InputWrapper } from './InputWrapper.js';
import { Label } from './Label.js';
import { Prefix } from './Prefix.js';
import { Suffix } from './Suffix.js';
import {
  type LabelPosition,
  LabelVariant,
  TextInputPrefix,
  type TextInputSize,
  type TextInputValue,
  type TextInputVariant,
} from './types.js';
import { type IconSvgProps } from '@contra/icons/icons';
import {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  RefCallback,
  RefObject,
  useRef,
} from 'react';
import { styled } from 'styled-components';

const Container = styled.label`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OuterLabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;

  & > :last-child {
    line-height: 1;
  }
`;

export type TextInputProps<TVariant extends TextInputVariant> = {
  readonly action?: JSX.Element;
  readonly additionalWrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  readonly allowDecimals?: TVariant extends 'usd'
    ? boolean
    : TVariant extends 'number'
      ? boolean
      : TVariant extends 'percent'
        ? boolean
        : never;
  readonly ariaLabel?: string;
  readonly autoComplete?: string;
  readonly autoFocus?: boolean;
  readonly disabled?: boolean;
  readonly error?: null | string;
  readonly helperText?: null | ReactNode | string;
  readonly hideNumberIncrementMenu?: TVariant extends 'number'
    ? boolean
    : never;
  readonly hideSuffix?: boolean;
  readonly id?: string;
  readonly inputRef?:
    | RefCallback<HTMLInputElement>
    | RefObject<HTMLInputElement>;
  readonly isMenuOpen?: TVariant extends 'menu' ? boolean : never;
  readonly label?: {
    readonly color?: ThemeColorsV2;
    readonly position?: LabelPosition;
    readonly subtitle?: string;
    readonly text: ReactNode | string;
    readonly variant?: LabelVariant;
  };
  readonly labelPrefix?: ReactNode;
  // this is meant for use only with amounts for cases when the amount
  // should be large and centered and should have the prefix visible in the same
  // font size as the amount
  readonly largeAndCentered?: TVariant extends 'usd' ? boolean : never;
  readonly max?: TVariant extends 'number' ? number : never;
  readonly maxCharacters?: TVariant extends 'text' | 'textArea'
    ? number
    : never;
  readonly min?: TVariant extends 'number'
    ? number
    : TVariant extends 'date'
      ? string
      : never;
  readonly name?: string;
  readonly onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Opinionated version of onChange that handles a lot of heavy lifting
   * but may prove incompatible with other opinionated frameworks like Downshift
   */
  readonly onValueChange?: (value?: TextInputValue<TVariant>) => void;
  readonly placeholder?: string;
  readonly prefix?: TextInputPrefix;
  readonly readOnly?: boolean;
  readonly resize?: TVariant extends 'textArea'
    ? 'both' | 'horizontal' | 'none' | 'vertical' | null
    : never;
  readonly rows?: TVariant extends 'textArea' ? number : never;
  readonly size?: TextInputSize;
  readonly suffix?:
    | string
    | {
        customSize?: TextInputSize;
        icon: React.FunctionComponent<IconSvgProps>;
        label: string;
        onClick?: () => void;
        tooltip?: string;
      };
  readonly value?: TextInputValue<TVariant>;
  readonly variant: TVariant;
};

export const TextInput = <TVariant extends TextInputVariant>({
  action,
  additionalWrapperProps,
  allowDecimals,
  ariaLabel,
  autoComplete,
  autoFocus,
  disabled = false,
  error = null,
  helperText,
  hideNumberIncrementMenu,
  hideSuffix = false,
  id,
  inputRef,
  isMenuOpen,
  label,
  labelPrefix,
  largeAndCentered,
  max,
  maxCharacters,
  min,
  name,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onValueChange,
  placeholder,
  prefix,
  readOnly = false,
  resize,
  rows,
  size = 'medium',
  suffix,
  value,
  variant,
}: TextInputProps<TVariant>) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const referencedRef = inputRef ?? internalRef;

  if (['card', 'search'].includes(variant)) {
    throw new Error('Not implemented');
  }

  const labelPosition = label?.position ?? 'outer';
  const labelVariant = label?.variant ?? 'default';

  return (
    <Container>
      {labelPosition === 'outer' ? (
        labelPrefix ? (
          <OuterLabelRow>
            {labelPrefix}
            <Label
              color={label?.color}
              inputSize={size}
              position="outer"
              subtitle={label?.subtitle}
              text={label?.text}
              variant={labelVariant}
            />
          </OuterLabelRow>
        ) : (
          <Label
            color={label?.color}
            inputSize={size}
            position="outer"
            subtitle={label?.subtitle}
            text={label?.text}
            variant={labelVariant}
          />
        )
      ) : null}
      <InputWrapper
        {...additionalWrapperProps}
        disabled={disabled}
        hasError={Boolean(error)}
        isInteractive={variant === 'menu'}
        labelPosition={labelPosition}
        largeAndCentered={largeAndCentered}
        readOnly={readOnly}
        size={size}
        variant={variant}
      >
        {labelPosition === 'inner' && (
          <Label
            color={label?.color}
            inputSize={size}
            position="inner"
            text={label?.text}
            variant={labelVariant}
          />
        )}
        <InputValueWrapper>
          {largeAndCentered ? null : (
            <Prefix<TVariant>
              disabled={disabled}
              prefix={prefix}
              size={size}
              variant={variant}
            />
          )}
          <Input<TVariant>
            allowDecimals={allowDecimals}
            ariaLabel={ariaLabel}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            disabled={disabled}
            hasError={Boolean(error)}
            id={id}
            inputRef={referencedRef}
            labelText={label?.text}
            largeAndCentered={largeAndCentered}
            max={max}
            min={min}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onValueChange={onValueChange}
            placeholder={placeholder}
            readOnly={readOnly}
            value={value}
            variant={variant}
            {...(variant === 'textArea' ? { resize, rows } : {})}
            hasActionArea={Boolean(action)}
          />
          {hideSuffix ? null : (
            <Suffix<TVariant>
              disabled={disabled}
              hideNumberIncrementMenu={hideNumberIncrementMenu}
              isMenuOpen={isMenuOpen}
              max={max}
              min={min}
              onValueChange={onValueChange}
              readOnly={readOnly}
              size={size}
              suffix={suffix}
              value={value}
              variant={variant}
            />
          )}
        </InputValueWrapper>
      </InputWrapper>
      <HelperText<TVariant>
        disabled={disabled}
        error={error}
        helperText={helperText}
        maxCharacters={maxCharacters}
        value={value}
        variant={variant}
      />
      {action ? action : undefined}
    </Container>
  );
};
